import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import useGamepad from "@/hooks/useGamepad";
import useTimer from "@/hooks/useTimer";
import useCooldown from "@/hooks/useCooldown";
import { useAuth } from "@/context/AuthContext";
import { loadStats, updateStats } from "@/utils/storage";
import { play as sfx } from "@/utils/sounds";

export const slugify = (value) => value.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const keyMap = {
  ArrowUp: "up",
  ArrowDown: "down",
  ArrowLeft: "left",
  ArrowRight: "right",
  w: "up",
  s: "down",
  a: "left",
  d: "right",
};

const STATUS_CONFIG = {
  success: { text: "Stratagem Authorized", color: "text-green-400" },
  fail: { text: "Treason Detected", color: "text-red-400" },
};
const DEFAULT_STATUS = { text: "Awaiting Input", color: "text-gray-400" };

function getStatusDetail(isLocked, cooldownMs, lastResult, longestStreak) {
  if (isLocked) return `Cooldown: ${(cooldownMs / 1000).toFixed(1)}s`;
  if (lastResult === "success") return `Best streak: ${longestStreak || 0}`;
  if (lastResult === "fail") return "Resetting input buffer";
  return "Enter the sequence cleanly";
}

const usePracticeSession = (stratagems) => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState("");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [inputSequence, setInputSequence] = useState([]);
  const [startTime, setStartTime] = useState(null);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [attemptsSession, setAttemptsSession] = useState(0);
  const [successesSession, setSuccessesSession] = useState(0);
  const [totalTimeSession, setTotalTimeSession] = useState(0);
  const [totalInputsSession, setTotalInputsSession] = useState(0);
  const [lastResult, setLastResult] = useState(null);
  const [lastTimeMs, setLastTimeMs] = useState(null);
  const [stats, setStats] = useState(loadStats());
  const [serverStats, setServerStats] = useState(null);
  const [lockUntil, setLockUntil] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const successTimeoutRef = useRef(null);

  const elapsed = useTimer(startTime);
  const { cooldownMs, isLocked } = useCooldown(lockUntil);

  const categories = useMemo(() => {
    const unique = new Set();
    stratagems.forEach((item) => {
      if (item.category) unique.add(item.category);
      if (item.categories) item.categories.forEach((c) => unique.add(c));
    });
    return ["all", "new", ...Array.from(unique).sort()];
  }, [stratagems]);

  const newCount = useMemo(
    () => stratagems.filter((item) => item.is_new).length,
    [stratagems],
  );

  const filtered = useMemo(() => {
    return stratagems.filter((item) => {
      const matchesSearch = item.name.toLowerCase().includes(search.toLowerCase());
      if (category === "new") return matchesSearch && item.is_new;
      if (category === "all") return matchesSearch;
      const matchesCategory =
        item.category === category ||
        (item.categories && item.categories.includes(category));
      return matchesSearch && matchesCategory;
    });
  }, [stratagems, search, category]);

  const selected = useMemo(
    () => stratagems.find((item) => item.id === selectedId) || null,
    [stratagems, selectedId],
  );

  useEffect(() => {
    const target = searchParams.get("target");
    if (target && stratagems.length) setSelectedId(target);
  }, [searchParams, stratagems]);

  const resetSession = useCallback(() => {
    setInputSequence([]);
    setStartTime(null);
    setCurrentStreak(0);
    setAttemptsSession(0);
    setSuccessesSession(0);
    setTotalTimeSession(0);
    setTotalInputsSession(0);
    setLastResult(null);
    setLastTimeMs(null);
    setLockUntil(null);
    setShowSuccess(false);
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = null;
    }
  }, []);

  const recordAttempt = useCallback(
    ({ success, timeMs, inputsCount, nextStreak }) => {
      setAttemptsSession((p) => p + 1);
      if (success) {
        setSuccessesSession((p) => p + 1);
        setTotalTimeSession((p) => p + timeMs);
        setTotalInputsSession((p) => p + inputsCount);
      }
      setLastResult(success ? "success" : "fail");
      setLastTimeMs(timeMs);
      setStats(
        updateStats({
          stratagem: selected,
          success,
          timeMs,
          inputsCount,
          streak: nextStreak,
        }),
      );
      // Submit to server only for logged-in users (fire-and-forget)
      if (selected && user) {
        axios
          .post(`${API}/practice/submit`, {
            stratagem_id: selected.id,
            success,
            time_ms: timeMs,
            inputs_count: inputsCount,
            streak: nextStreak,
          })
          .catch(() => {});
      }
    },
    [selected, user],
  );

  const clearSuccessState = useCallback(() => {
    setInputSequence([]);
    setStartTime(null);
    setShowSuccess(false);
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = null;
    }
  }, []);

  const handleInput = useCallback(
    (direction) => {
      if (!selected) return;
      if (lockUntil && Date.now() < lockUntil) return;
      if (showSuccess) clearSuccessState();

      const now = Date.now();
      const targetSequence = selected.code;
      const currentIndex = showSuccess ? 0 : inputSequence.length;
      const expected = targetSequence[currentIndex];
      const startedAt = startTime || now;

      if (currentIndex === 0) setStartTime(now);

      if (direction !== expected) {
        const failTime = now - startedAt;
        sfx("fail");
        setInputSequence([]);
        setStartTime(null);
        setCurrentStreak(0);
        setLockUntil(now + 500);
        recordAttempt({
          success: false,
          timeMs: failTime,
          inputsCount: currentIndex + 1,
          nextStreak: 0,
        });
        toast.error("Treason detected — sequence failed.");
        return;
      }

      const nextSequence = showSuccess
        ? [direction]
        : [...inputSequence, direction];
      if (nextSequence.length === targetSequence.length) {
        const timeMs = now - startedAt;
        const nextStreak = currentStreak + 1;
        sfx(nextStreak >= 3 ? "perfect" : "complete");
        setInputSequence(nextSequence);
        setShowSuccess(true);
        setCurrentStreak(nextStreak);
        recordAttempt({
          success: true,
          timeMs,
          inputsCount: targetSequence.length,
          nextStreak,
        });
        toast.success("Stratagem authorized.");
        const timeout = setTimeout(() => {
          setInputSequence([]);
          setStartTime(null);
          setShowSuccess(false);
          successTimeoutRef.current = null;
        }, 150);
        successTimeoutRef.current = timeout;
      } else {
        sfx(`input_${direction}`);
        setInputSequence(nextSequence);
      }
    },
    [
      clearSuccessState,
      currentStreak,
      inputSequence,
      recordAttempt,
      selected,
      showSuccess,
      startTime,
      lockUntil,
    ],
  );

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (["INPUT", "SELECT", "TEXTAREA"].includes(event.target.tagName)) return;
      const direction = keyMap[event.key];
      if (direction) {
        event.preventDefault();
        handleInput(direction);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleInput]);

  useEffect(() => {
    setIsTouchDevice("ontouchstart" in window || navigator.maxTouchPoints > 0);
    return () => {
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    };
  }, []);

  // Fetch server-side stats for logged-in users
  useEffect(() => {
    if (!user) {
      setServerStats(null);
      return;
    }
    axios
      .get(`${API}/practice/stats`)
      .then((res) => setServerStats(res.data))
      .catch(() => setServerStats(null));
  }, [user]);

  const { connected: gamepadConnected } = useGamepad({
    enabled: true,
    onDirection: handleInput,
  });

  const sessionAccuracy = attemptsSession
    ? successesSession / attemptsSession
    : 0;
  const sessionAvg = successesSession
    ? totalTimeSession / successesSession
    : 0;
  const sessionInputsPerMin = totalTimeSession
    ? Math.round(totalInputsSession / (totalTimeSession / 60000))
    : 0;

  // Lifetime stats: prefer server-side when logged in, fallback to localStorage
  const lifetimeSource = useMemo(() => {
    if (serverStats && selected) {
      const entry = serverStats.by_stratagem?.[selected.id];
      return entry || null;
    }
    return selected ? stats.byStratagem[selected.id] : null;
  }, [serverStats, selected, stats]);

  const lifetimeAccuracy =
    lifetimeSource && lifetimeSource.attempts
      ? (lifetimeSource.successes || 0) / lifetimeSource.attempts
      : 0;
  const lifetimeAvg =
    lifetimeSource && lifetimeSource.successes
      ? (lifetimeSource.total_time_ms || lifetimeSource.totalTimeMs || 0) / lifetimeSource.successes
      : 0;
  const lifetimeBestTime =
    lifetimeSource
      ? lifetimeSource.best_time_ms ?? lifetimeSource.bestTimeMs ?? null
      : null;
  const overallLongestStreak = serverStats
    ? serverStats.overall?.longest_streak || 0
    : stats.overall.longestStreak || 0;

  const { text: statusText, color: statusColor } =
    STATUS_CONFIG[lastResult] || DEFAULT_STATUS;
  const statusDetail = getStatusDetail(
    isLocked,
    cooldownMs,
    lastResult,
    overallLongestStreak,
  );

  return {
    selectedId,
    setSelectedId,
    search,
    setSearch,
    category,
    setCategory,
    categories,
    newCount,
    filtered,
    selected,
    inputSequence,
    elapsed,
    currentStreak,
    showSuccess,
    isLocked,
    cooldownMs,
    lastResult,
    lastTimeMs,
    attemptsSession,
    successesSession,
    sessionAccuracy,
    sessionInputsPerMin,
    sessionAvg,
    lifetimeAccuracy,
    lifetimeAvg,
    lifetimeBestTime,
    lifetimeSource,
    stats,
    statusText,
    statusColor,
    statusDetail,
    handleInput,
    resetSession,
    isTouchDevice,
    gamepadConnected,
  };
};

export default usePracticeSession;
